import type { WppChat, ChatFilter } from "@/hooks/useWhatsAppChat";
import { useTranslation } from "react-i18next";
import ChatListShimmer from "./ChatListShimmer";

interface ChatListSidebarProps {
  chats: WppChat[];
  archivedChats: WppChat[];
  selectedChat: WppChat | null;
  loading: boolean;
  error: string | null;
  filter: ChatFilter;
  searchTerm: string;
  showArchived: boolean;
  lastMessages: Record<string, { body: string; type: string; fromMe: boolean; t: number; senderName?: string }>;
  onFilterChange: (f: ChatFilter) => void;
  onSearchChange: (s: string) => void;
  onSelectChat: (chat: WppChat) => void;
  onToggleArchived: () => void;
  onRetry: () => void;
  onBulkClick?: () => void;
  onCrmClick?: () => void;
  onCatalogClick?: () => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
}

function getChatName(chat: WppChat): string {
  return (
    chat.contact?.name ||
    chat.contact?.pushname ||
    chat.contact?.formattedName ||
    chat.contact?.shortName ||
    chat.groupMetadata?.subject ||
    chat.id.user ||
    "Unknown"
  );
}

function getTypeLabel(type: string, t: (k: string) => string): string {
  const typeLabels: Record<string, string> = {
    image: "chatList.photo",
    photo: "chatList.photo",
    video: "chatList.video",
    ptt: "chatList.voiceMessage",
    audio: "chatList.audio",
    document: "chatList.document",
    location: "chatList.location",
    sticker: "chatList.sticker",
    vcard: "chatList.contact",
    contact: "chatList.contact",
    poll: "chatList.poll",
    poll_creation: "chatList.poll",
    gif: "chatList.gif",
    product: "chatList.product",
    catalog: "chatList.catalog",
    order: "chatList.order",
    template: "chatList.template",
    button: "chatList.button",
    interactive: "chatList.interactiveMessage",
    ephemeral: "chatList.disappearingMessage",
    gp2: "chatList.groupEvent",
    notification_template: "chatList.notification",
    broadcast: "chatList.broadcast",
  };
  const key = typeLabels[type];
  return key ? t(key) : type;
}

function getTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    image: "ri-image-line",
    photo: "ri-image-line",
    video: "ri-video-line",
    ptt: "ri-mic-line",
    audio: "ri-volume-up-line",
    document: "ri-file-line",
    location: "ri-map-pin-line",
    sticker: "ri-sticky-note-line",
    vcard: "ri-user-line",
    contact: "ri-user-line",
    poll: "ri-bar-chart-line",
    poll_creation: "ri-bar-chart-line",
    gif: "ri-file-gif-line",
    product: "ri-shopping-bag-line",
    catalog: "ri-store-line",
    order: "ri-receipt-line",
    template: "ri-layout-line",
    button: "ri-cursor-line",
    interactive: "ri-chat-smile-line",
    ephemeral: "ri-timer-line",
    gp2: "ri-group-line",
    notification_template: "ri-notification-line",
    broadcast: "ri-speaker-line",
  };
  return icons[type] || "";
}

function getLastMessagePreview(
  chat: WppChat,
  apiLastMsg: { body: string; type: string; fromMe: boolean; t: number; senderName?: string } | undefined,
  t: (k: string) => string
): { text: string; fromMe: boolean; icon?: string; senderName?: string } {
  if (apiLastMsg) {
    const { body, type, fromMe, senderName } = apiLastMsg;
    if (type === "call_log") {
      const bodyLower = (body || "").toLowerCase();
      const isVideo = bodyLower.includes("video");
      const icon = isVideo ? "ri-video-line" : "ri-phone-line";
      return { text: body || getTypeLabel(isVideo ? "video" : "ptt", t), fromMe, icon, senderName };
    }
    const knownTypes = ["image", "photo", "video", "ptt", "audio", "document", "location", "sticker", "vcard", "contact", "poll", "poll_creation", "gif", "product"];
    if (knownTypes.includes(type) || getTypeLabel(type, t) !== type) {
      return { text: getTypeLabel(type, t), fromMe, icon: getTypeIcon(type), senderName };
    }
    if (body) return { text: body, fromMe, senderName };
    return { text: type ? `Message (${type})` : "", fromMe, senderName };
  }

  const preview = chat.chatlistPreview;
  if (!preview) return { text: "", fromMe: false };

  const fromMe = chat.lastReceivedKey?.fromMe ?? false;

  if (preview.type === "reaction" && preview.reactionText) {
    return { text: preview.reactionText, fromMe: false };
  }

  if (preview.type === "chat" || preview.type === "text") {
    return { text: preview.body || "", fromMe, senderName: preview.sender || undefined };
  }

  if (preview.type === "call_log" || preview.type === "call") {
    const bodyLower = (preview.body || "").toLowerCase();
    const isVideo = bodyLower.includes("video");
    const icon = isVideo ? "ri-video-line" : "ri-phone-line";
    const label = preview.body || getTypeLabel(isVideo ? "video" : "ptt", t);
    return { text: label, fromMe, icon, senderName: preview.sender || undefined };
  }

  const typeIcon = getTypeIcon(preview.type || "");
  const typeLabel = getTypeLabel(preview.type || "", t);
  if (typeLabel !== (preview.type || "")) {
    return { text: typeLabel, fromMe, icon: typeIcon, senderName: preview.sender || undefined };
  }

  if (preview.body) {
    return { text: preview.body, fromMe, senderName: preview.sender || undefined };
  }

  return { text: preview.type ? `Message (${preview.type})` : "", fromMe, senderName: preview.sender || undefined };
}

function formatChatTime(timestamp: number): string {
  if (!timestamp) return "";
  const now = new Date();
  const date = new Date(timestamp * 1000);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
  }
  // Return "Yesterday" - will be translated in component
  if (diffDays === 1) return "YESTERDAY_PLACEHOLDER";
  if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" });
  }
  return date.toLocaleDateString([], { day: "numeric", month: "numeric", year: "2-digit" });
}

function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function stringToHue(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
}

export default function ChatListSidebar({
  chats,
  archivedChats,
  selectedChat,
  loading,
  error,
  filter,
  searchTerm,
  showArchived,
  lastMessages,
  onFilterChange,
  onSearchChange,
  onSelectChat,
  onToggleArchived,
  onRetry,
  onBulkClick,
  onCrmClick,
  onCatalogClick,
  hasMore,
  onLoadMore,
}: ChatListSidebarProps) {
  const { t } = useTranslation("dashboard");

  const FILTERS: { key: ChatFilter; label: string; icon: string }[] = [
    { key: "all", label: t("chatList.filterAll"), icon: "ri-message-2-line" },
    { key: "unread", label: t("chatList.filterUnread"), icon: "ri-mail-unread-line" },
    { key: "groups", label: t("chatList.filterGroups"), icon: "ri-group-line" },
  ];

  return (
    <div className="w-full lg:w-[380px] xl:w-[420px] flex-shrink-0 flex flex-col h-full border-r border-background-200/60 bg-background-50">
      <div className="px-3 pt-3 pb-2 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-base text-foreground-950">{t("chatList.title")}</h3>
          <div className="flex items-center gap-1.5">
            <button
              onClick={onBulkClick}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
              title={t("chatList.bulkMessage")}
            >
              <i className="ri-mail-send-line text-sm"></i>
            </button>
            <button
              onClick={onCrmClick}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
              title={t("chatList.crmPipeline")}
            >
              <i className="ri-folder-user-line text-sm"></i>
            </button>
            <button
              onClick={onCatalogClick}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
              title={t("chatList.catalog")}
            >
              <i className="ri-store-2-line text-sm"></i>
            </button>
            <button
              onClick={onRetry}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 cursor-pointer transition-colors"
              title={t("chatList.refreshChats")}
            >
              <i className="ri-refresh-line text-sm"></i>
            </button>
          </div>
        </div>

        <div className="relative mb-2">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <i className="ri-search-line text-foreground-400 text-sm"></i>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("chatList.searchPlaceholder")}
            className="w-full pl-9 pr-4 py-2 rounded-lg bg-background-100 border border-background-200/50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-secondary-400 focus:ring-1 focus:ring-secondary-300 transition-colors"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute inset-y-0 right-3 flex items-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => onFilterChange(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                filter === f.key
                  ? "bg-secondary-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className={`${f.icon} text-[11px]`}></i>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto overflow-x-hidden"
        onScroll={(e) => {
          const target = e.currentTarget;
          if (target.scrollHeight - target.scrollTop <= target.clientHeight + 100) {
            if (hasMore && onLoadMore) {
              onLoadMore();
            }
          }
        }}
      >
        {loading && chats.length === 0 && (
          <ChatListShimmer />
        )}

        {error && chats.length === 0 && (
          <div className="px-4 py-6 text-center">
            <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-500 text-sm"></i>
            </div>
            <p className="text-foreground-500 text-xs mb-3">{error}</p>
            <button
              onClick={onRetry}
              className="text-xs text-secondary-600 hover:text-secondary-700 font-medium cursor-pointer"
            >
              {t("chatList.tryAgain")}
            </button>
          </div>
        )}

        {!loading && !error && chats.length === 0 && (
          <div className="px-4 py-10 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-chat-3-line text-foreground-400 text-lg"></i>
            </div>
            <p className="text-foreground-500 text-xs">{t("chatList.noChats")}</p>
          </div>
        )}

        {chats.map((chat) => {
          const isSelected =
            selectedChat?.id._serialized === chat.id._serialized;
          const name = getChatName(chat);
          const preview = getLastMessagePreview(chat, lastMessages[chat.id._serialized], t);
          const hue = stringToHue(chat.id._serialized);
          const initials = getInitials(name);
          const avatarUrl = chat.contact?.profilePicThumbObj?.img;

          return (
            <div
              key={chat.id._serialized}
              onClick={() => onSelectChat(chat)}
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors border-b border-background-100/60 hover:bg-background-100/50 ${
                isSelected ? "bg-background-200/60" : ""
              }`}
            >
              <div className="flex-shrink-0 relative">
                {avatarUrl ? (
                  <div className="w-12 h-12 rounded-full overflow-hidden">
                    <img
                      src={avatarUrl}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                    style={{
                      background: `oklch(0.55 0.15 ${hue})`,
                    }}
                  >
                    {initials}
                  </div>
                )}
                {chat.pin > 0 && (
                  <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-background-50 flex items-center justify-center">
                    <i className="ri-pushpin-fill text-foreground-500 text-[10px]"></i>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground-950 truncate">
                    {name}
                  </span>
                  <span className="text-[10px] text-foreground-400 flex-shrink-0 whitespace-nowrap">
                    {formatChatTime(chat.t) === "YESTERDAY_PLACEHOLDER" ? t("chatList.yesterday") : formatChatTime(chat.t)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-foreground-500 truncate flex items-center gap-1">
                    {preview.fromMe && (
                      <span className="text-foreground-600 font-medium flex-shrink-0">{t("chatList.you")}:</span>
                    )}
                    {!preview.fromMe && chat.isGroup && preview.senderName && (
                      <span className="text-foreground-600 font-medium flex-shrink-0">{preview.senderName}:</span>
                    )}
                    {preview.icon && (
                      <i className={`${preview.icon} text-[11px] text-foreground-400 flex-shrink-0`}></i>
                    )}
                    {preview.text}
                  </span>
                  {chat.unreadCount > 0 && (
                    <span className="flex-shrink-0 min-w-[18px] h-[18px] rounded-full bg-accent-500 text-white text-[10px] font-semibold flex items-center justify-center px-1">
                      {chat.unreadCount > 99 ? "99+" : chat.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {hasMore && (
          <div className="py-4 text-center">
            <i className="ri-loader-4-line animate-spin text-foreground-400 text-lg"></i>
          </div>
        )}
      </div>

      {archivedChats.length > 0 && (
        <div className="flex-shrink-0 border-t border-background-200/60">
          <button
            onClick={onToggleArchived}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i
              className={`text-sm transition-transform ${
                showArchived ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"
              }`}
            ></i>
            <span>{t("chatList.archived")}</span>
            <span className="text-foreground-400 ml-auto">
              {archivedChats.length}
            </span>
          </button>

          {showArchived && (
            <div className="max-h-[200px] overflow-y-auto">
              {archivedChats.map((chat) => {
                const name = getChatName(chat);
                const hue = stringToHue(chat.id._serialized);
                const initials = getInitials(name);
                const avatarUrl = chat.contact?.profilePicThumbObj?.img;

                return (
                  <div
                    key={chat.id._serialized}
                    onClick={() => onSelectChat(chat)}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-background-100/50 transition-colors border-b border-background-100/30"
                  >
                    {avatarUrl ? (
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={avatarUrl}
                          alt={name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                        style={{
                          background: `oklch(0.55 0.12 ${hue})`,
                        }}
                      >
                        {initials}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-foreground-700 truncate">
                          {name}
                        </span>
                        <span className="text-[10px] text-foreground-400 flex-shrink-0 whitespace-nowrap">
                          {formatChatTime(chat.t) === "YESTERDAY_PLACEHOLDER" ? t("chatList.yesterday") : formatChatTime(chat.t)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}